import {
  IconButton,
  TableCell,
  TableRow,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { BasicTable } from '@components';
import {
  FuelSurchargeRange,
  fuelSurchargeStore,
  setFuelSurchargeStore,
} from '@store/customers/customerDetails/fuelSurcharge';
import { PlusCircleOutlineIcon, DeleteIcon } from '@atgfr8/icons';
import { isAdmin } from '@utils/utils';
import { Index, Show } from 'solid-js';

interface SurchargeTableProps {
  activeTab: number;
  isDefault: boolean;
  addNewItem: () => void;
}

export const SurchargeTable = (props: SurchargeTableProps) => {
  const ltlColumns = [
    {
      key: 'min',
      name: 'Min',
      renderCell: (row: FuelSurchargeRange) => `$${Number(row.min).toFixed(2)}`,
    },
    {
      key: 'max',
      name: 'Max',
      renderCell: (row: FuelSurchargeRange) => `$${Number(row.max).toFixed(2)}`,
    },
    {
      key: 'surchargeModifier',
      name: 'Surcharge Percentage',
      renderCell: (row: FuelSurchargeRange) => {
        if (props.activeTab === 0) {
          return `${(row.surchargeModifier * 100).toFixed(2)}%`;
        }
      },
    },
  ];

  const loadColumns = [
    {
      key: 'min',
      name: 'Min',
      renderCell: (row: FuelSurchargeRange) => `$${Number(row.min).toFixed(2)}`,
    },
    {
      key: 'max',
      name: 'Max',
      renderCell: (row: FuelSurchargeRange) => `$${Number(row.max).toFixed(2)}`,
    },
    {
      key: 'surchargeModifier',
      name: 'Surcharge / Mile',
      renderCell: (row: FuelSurchargeRange) => {
        return `$${row.surchargeModifier}`;
      },
    },
  ];

  const footerRow = [
    {
      renderCell: () => (
        <span>
          <PlusCircleOutlineIcon
            color="#0070A2"
            style={{ cursor: 'pointer' }}
            class="inline-block"
            onClick={props.addNewItem}
          />
          Add Range
        </span>
      ),
      key: 'ideaTitle',
    },
  ];

  const handleRowChange = (
    index: number,
    value: string,
    key: keyof FuelSurchargeRange,
  ) => {
    const data = [
      ...(fuelSurchargeStore.fuelSurchargeData?.fuelSurchargeRanges || []),
    ];

    if (data[index].operationType !== 'Insert') {
      data[index] = {
        ...data[index],
        [key]: value,
        operationType: 'Update',
      };
    } else {
      data[index] = {
        ...data[index],
        [key]: value,
      };
    }
    setFuelSurchargeStore('fuelSurchargeData', 'fuelSurchargeRanges', data);
  };

  const onDeleteClick = (index: number) => {
    const data = [
      ...(fuelSurchargeStore.fuelSurchargeData?.fuelSurchargeRanges || []),
    ];
    if (data[index].operationType === 'Insert') {
      data.splice(index, 1);
    } else {
      data[index] = {
        ...data[index],
        operationType: 'Delete',
      };
    }
    setFuelSurchargeStore('fuelSurchargeData', 'fuelSurchargeRanges', data);
  };

  const preventingNonNumeric = (e: KeyboardEvent) => {
    if (e.key === 'e' || e.key === '-' || e.key === '+') {
      e.preventDefault();
    }
  };

  const handleDecimalVal = (
    val: string,
    index: number,
    fieldName: keyof FuelSurchargeRange,
  ) => {
    let inputValue = val.replace(/[^0-9.]/g, '');
    if (inputValue.length > 2 && !inputValue.includes('.')) {
      inputValue = inputValue.slice(0, 2) + '.' + inputValue.slice(2);
    }
    if (inputValue.includes('.')) {
      const parts = inputValue.split('.');
      if (parts[1].length > 2) {
        inputValue = parts[0] + '.' + parts[1].slice(0, 2);
      }
    }
    handleRowChange(index, inputValue, fieldName);
  };

  const clearZeroValueOnFocus = (
    event: FocusEvent & { currentTarget: HTMLInputElement },
    index: number,
    fieldName: 'min' | 'max',
  ) => {
    if (event.currentTarget.value === '0') {
      handleRowChange(index, '', fieldName);
    }
  };

  return (
    <>
      <Show when={props.isDefault}>
        <BasicTable<FuelSurchargeRange>
          tableContainerClasses="overflow-auto w-[70%]! border mt-4 bg-white text-black! [&_td]:!border-[#e0e0e0] [&_th]:!border-[#e0e0e0]"
          tableHeaderClasses="!sticky top-0 bg-white shadow-xs"
          cellClasses="!text-black"
          columns={props.activeTab === 0 ? ltlColumns : loadColumns}
          rows={(
            fuelSurchargeStore.fuelSurchargeData?.fuelSurchargeRanges ?? []
          ).filter((item) => item.type === props.activeTab)}
        />
      </Show>
      <Show when={!props.isDefault}>
        <BasicTable<FuelSurchargeRange>
          columns={props.activeTab === 0 ? ltlColumns : loadColumns}
          footerRow={isAdmin() ? footerRow : undefined}
          cellClasses="leading-none"
        >
          <Index
            each={
              fuelSurchargeStore.fuelSurchargeData?.fuelSurchargeRanges || []
            }
          >
            {(rowData, index) => (
              <Show when={rowData().operationType !== 'Delete'}>
                <TableRow
                  class={rowData().type === props.activeTab ? '' : 'hidden!'}
                >
                  <TableCell>
                    <TextField
                      type="text"
                      value={String(rowData().min)}
                      on:focus={(
                        event: FocusEvent & { currentTarget: HTMLInputElement },
                      ) => {
                        clearZeroValueOnFocus(event, index, 'min');
                      }}
                      onChange={(val: string) =>
                        handleDecimalVal(val, index, 'min')
                      }
                      size="sm"
                      maxLength={6}
                      disabled={!isAdmin()}
                      onKeyPress={preventingNonNumeric}
                      dataTestId={`fuel-surcharge-min-textInput-${index}`}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="text"
                      value={String(rowData().max)}
                      on:focus={(
                        event: FocusEvent & { currentTarget: HTMLInputElement },
                      ) => {
                        clearZeroValueOnFocus(event, index, 'max');
                      }}
                      onChange={(val: string) =>
                        handleDecimalVal(val, index, 'max')
                      }
                      size="sm"
                      disabled={!isAdmin()}
                      onKeyPress={preventingNonNumeric}
                      dataTestId={`fuel-surcharge-max-textInput-${index}`}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={String(rowData().surchargeModifier)}
                      endIcon={props.activeTab === 0 && '%'}
                      startIcon={props.activeTab === 1 ? '$' : undefined}
                      onKeyPress={preventingNonNumeric}
                      onChange={(val: string) => {
                        handleRowChange(index, val, 'surchargeModifier');
                      }}
                      size="sm"
                      maxLength={6}
                      disabled={!isAdmin()}
                      dataTestId={`fuel-surcharge-surcharge-modifier-textInput-${index}`}
                    />
                  </TableCell>
                  <Show when={isAdmin()}>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          onDeleteClick(index);
                        }}
                        dataTestId="surcharge-tables-delete-icon-button"
                      >
                        <DeleteIcon color="#B00020" />
                      </IconButton>
                    </TableCell>
                  </Show>
                </TableRow>
              </Show>
            )}
          </Index>
        </BasicTable>
      </Show>
    </>
  );
};
